// src/TaskOptimizer.js
import React, { useState, useEffect, useRef, useCallback } from "react";

/* ----------------------------- Local persistence ---------------------------- */
const LS_KEY = "syncusive_task_optimizer_v1";
const readLS = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeLS = (data) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {}
};

/* ----------------------------- Backend base URL ---------------------------- */
// Use relative URLs in all local-dev scenarios so the CRA proxy routes requests
// to localhost:5000, avoiding CORS errors regardless of the browser origin
// (localhost:3000 vs 127.0.0.1:3000).
const API_BASE =
  (typeof process !== "undefined" && process.env.REACT_APP_API_BASE) || "";

/* --------------------------------- Auth Flow -------------------------------- */
const startGoogleLogin = () => {
  try {
    const current =
      window.location.pathname +
      window.location.search +
      window.location.hash;
    localStorage.setItem("syncusive_post_login_path", current);
  } catch {}
  const authStartUrl = API_BASE ? `${API_BASE}/api/auth/google/start` : "/api/auth/google/start";
  window.location.assign(authStartUrl);
};

/* ------------------------------ Design helpers ------------------------------ */
const ACCENTS = ["#22d3ee", "#a78bfa", "#2dd4bf", "#f472b6", "#fbbf24", "#60a5fa", "#34d399", "#fb923c"];
const BUDGET_PRESETS = [30, 60, 90, 120, 180, 240];
const CONSTRAINT_SUGGESTIONS = ["No paid tools", "Publish today", "Deep work first", "No meetings after 5pm", "Low energy day"];

const fmtMinutes = (m) => {
  const n = Math.max(0, Math.round(Number(m) || 0));
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const r = n % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};
const fmtOffset = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
const fmtClock = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

const timeAgo = (iso) => {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
};

// Next round 5-minute mark, used as the plan's start time
const nextFiveMinutes = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil((d.getMinutes() + 1) / 5) * 5);
  return d;
};

const SX_KEYFRAMES = `
  @keyframes sxDraw { 0% { stroke-dashoffset: 100 } 45% { stroke-dashoffset: 0 } 75% { stroke-dashoffset: 0 } 100% { stroke-dashoffset: -100 } }
  @keyframes sxSpin { to { transform: rotate(360deg) } }
  @keyframes sxSpinRev { to { transform: rotate(-360deg) } }
  @keyframes sxScan { from { transform: translateY(-24px) } to { transform: translateY(110px) } }
  @keyframes sxPulse { 0%, 100% { opacity: .5; transform: scale(.94) } 50% { opacity: 1; transform: scale(1.06) } }
  @keyframes sxFade { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
  @keyframes sxPop { 0% { transform: scale(.9) } 60% { transform: scale(1.06) } 100% { transform: scale(1) } }
  @keyframes authGridDrift { 0% { transform: translate(0,0) } 100% { transform: translate(40px,40px) } }
  @media (prefers-reduced-motion: reduce) { .sx-motion, .sx-motion * { animation: none !important; transition: none !important; } }
`;

/* ------------------------- Syncusive mark (animated) ------------------------ */
// Recreates the logo in SVG: pointy-top hexagon, triangular lattice and the angular "S".
const HEX_PTS = "50,4 89.84,27 89.84,73 50,96 10.16,73 10.16,27";
const S_PATH = "M67.8 37.8 L67.8 29.1 L50 19.4 L32.2 29.1 L32.2 40.3 L67.8 60.1 L67.8 70.9 L50 80.6 L32.2 70.9 L32.2 62.2";
const LATTICE = (() => {
  const lines = [];
  const w = 79.68 / 6; // column width across the hexagon
  for (let i = 1; i < 6; i++) {
    const x = 10.16 + i * w;
    lines.push([x, 0, x, 100]);
  }
  const k = Math.tan(Math.PI / 6);
  const h = w / Math.cos(Math.PI / 6); // vertical gap between parallel diagonals
  for (let n = -3; n <= 9; n++) {
    const c1 = 4 - k * 50 + n * h;
    const c2 = 4 + k * 50 + n * h;
    lines.push([0, c1, 100, k * 100 + c1]);
    lines.push([0, c2, 100, -k * 100 + c2]);
  }
  return lines;
})();

function SyncusiveMark({ done = false, className = "w-48 h-48" }) {
  const uid = React.useId().replace(/:/g, "");
  const draw = (delay) =>
    done
      ? { strokeDashoffset: 0, transition: "stroke-dashoffset .5s ease" }
      : { strokeDashoffset: 100, animation: `sxDraw 3.2s cubic-bezier(.65,0,.35,1) ${delay}s infinite` };

  return (
    <div className={`relative ${className}`} style={done ? { animation: "sxPop .5s ease" } : undefined}>
      <div
        className="absolute inset-[14%] rounded-full blur-2xl"
        style={{
          background: `radial-gradient(circle, ${done ? "rgba(52,211,153,0.5)" : "rgba(34,211,238,0.45)"}, transparent 70%)`,
          animation: "sxPulse 2.4s ease-in-out infinite",
        }}
      />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" style={{ animation: "sxSpin 16s linear infinite" }} aria-hidden="true">
        <circle cx="50" cy="50" r="48" fill="none" stroke="rgba(34,211,238,0.28)" strokeWidth="0.4" strokeDasharray="0.8 2.6" />
        <circle cx="50" cy="2" r="1.4" fill="#22d3ee" />
        <circle cx="91.6" cy="74" r="0.9" fill="#a78bfa" />
      </svg>
      <svg viewBox="0 0 100 100" className="absolute inset-[7%] w-[86%] h-[86%]" style={{ animation: "sxSpinRev 7s linear infinite" }} aria-hidden="true">
        <circle cx="50" cy="50" r="48" fill="none" stroke="#a78bfa" strokeOpacity="0.55" strokeWidth="0.7" strokeDasharray="34 268" strokeLinecap="round" />
        <circle cx="50" cy="50" r="48" fill="none" stroke="#22d3ee" strokeOpacity="0.7" strokeWidth="0.7" strokeDasharray="18 284" strokeDashoffset="-150" strokeLinecap="round" />
      </svg>
      <svg viewBox="0 0 100 100" className="absolute inset-[17%] w-[66%] h-[66%] overflow-visible" role="img" aria-label="Syncusive">
        <defs>
          <clipPath id={`${uid}-hex`}><polygon points={HEX_PTS} /></clipPath>
          <linearGradient id={`${uid}-s`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#67e8f9" />
            <stop offset="1" stopColor="#0ea5e9" />
          </linearGradient>
          <linearGradient id={`${uid}-band`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#22d3ee" stopOpacity="0" />
            <stop offset="0.5" stopColor="#22d3ee" stopOpacity="0.35" />
            <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
          </linearGradient>
          <filter id={`${uid}-glow`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>
        <g clipPath={`url(#${uid}-hex)`}>
          {LATTICE.map(([x1, y1, x2, y2], i) => (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#22d3ee" strokeOpacity="0.2" strokeWidth="0.35" />
          ))}
          {!done && <rect x="0" y="0" width="100" height="20" fill={`url(#${uid}-band)`} style={{ animation: "sxScan 2.6s linear infinite" }} />}
        </g>
        <polygon points={HEX_PTS} fill="none" stroke="#22d3ee" strokeOpacity="0.18" strokeWidth="0.8" />
        <polygon points={HEX_PTS} pathLength="100" fill="none" stroke="#22d3ee" strokeWidth="1.1" strokeLinejoin="round" strokeDasharray="100" filter={`url(#${uid}-glow)`} style={draw(0)} />
        <path d={S_PATH} fill="none" stroke="#22d3ee" strokeOpacity="0.1" strokeWidth="8" strokeLinejoin="miter" />
        <path d={S_PATH} pathLength="100" fill="none" stroke={`url(#${uid}-s)`} strokeWidth="8" strokeLinejoin="miter" strokeDasharray="100" filter={`url(#${uid}-glow)`} style={draw(0.35)} />
      </svg>
    </div>
  );
}

/* ------------------------- "Generating plan" overlay ------------------------ */
function OptimizingOverlay({ open, done, tasks, budget, constraintsCount, onCancel }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!open) return;
    const start = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed((Date.now() - start) / 1000), 100);
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      clearInterval(id);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onCancel]);

  if (!open) return null;

  const n = tasks.length;
  const stages = [
    `Reading your ${n} task${n === 1 ? "" : "s"}`,
    "Scoring impact × urgency",
    constraintsCount ? `Applying ${constraintsCount} constraint${constraintsCount === 1 ? "" : "s"}` : "Checking your context",
    `Fitting it into ${fmtMinutes(budget)}`,
    "Sequencing for momentum",
  ];
  const stageIdx = done ? stages.length : Math.min(stages.length - 1, Math.floor(elapsed / 1.6));
  const progress = done ? 100 : 94 * (1 - Math.exp(-elapsed / 6));
  const scanning = n ? Math.floor(elapsed / 0.8) % Math.min(n, 6) : -1;

  return (
    <div className="sx-motion fixed inset-0 z-[5000] flex items-center justify-center overflow-y-auto p-6" role="dialog" aria-modal="true" aria-label="Generating your plan">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(560px circle at 50% 38%, rgba(34,211,238,0.14), transparent 60%), radial-gradient(480px circle at 72% 82%, rgba(167,139,250,0.10), transparent 60%)" }}
      />

      <div className="relative w-full max-w-md flex flex-col items-center text-center py-6">
        <SyncusiveMark done={done} className="w-40 h-40 sm:w-52 sm:h-52" />

        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80">Syncusive AI</p>
        <h2 key={stageIdx} className="mt-2 text-xl sm:text-2xl font-bold text-white" style={{ animation: "sxFade .4s ease" }} aria-live="polite">
          {done ? "Your plan is ready" : `${stages[stageIdx]}…`}
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          {done ? `${n} task${n === 1 ? "" : "s"} ranked and time-boxed` : `Building a ranked plan for your ${fmtMinutes(budget)} window`}
        </p>

        <div className="w-full mt-6">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-teal-300 to-purple-400"
              style={{ width: `${progress}%`, boxShadow: "0 0 14px rgba(34,211,238,0.6)" }}
            />
          </div>
          <div className="mt-2 flex justify-between font-mono text-[11px] text-gray-500">
            <span>{Math.round(progress)}%</span>
            <span>{elapsed.toFixed(1)}s</span>
          </div>
        </div>

        <ul className="w-full mt-5 space-y-2 text-left">
          {stages.map((s, i) => {
            const state = i < stageIdx ? "done" : i === stageIdx ? "active" : "todo";
            return (
              <li key={s} className={`flex items-center gap-3 text-sm transition-colors ${state === "todo" ? "text-gray-600" : state === "active" ? "text-white" : "text-gray-400"}`}>
                <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                  {state === "done" ? (
                    <svg className="h-4 w-4 text-cyan-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                  ) : state === "active" ? (
                    <span className="h-4 w-4 rounded-full border-2 border-cyan-400/30 border-t-cyan-400 animate-spin" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-gray-700" />
                  )}
                </span>
                {s}
              </li>
            );
          })}
        </ul>

        {n > 0 && !done && (
          <div className="mt-5 flex flex-wrap justify-center gap-1.5">
            {tasks.slice(0, 6).map((t, i) => (
              <span
                key={i}
                className="max-w-[11rem] truncate rounded-full border px-2.5 py-1 text-[11px] transition-all duration-300"
                style={i === scanning
                  ? { borderColor: "rgba(34,211,238,0.6)", color: "#a5f3fc", background: "rgba(34,211,238,0.12)", boxShadow: "0 0 14px rgba(34,211,238,0.35)" }
                  : { borderColor: "rgba(255,255,255,0.1)", color: "#6b7280" }}
              >
                {t}
              </span>
            ))}
            {n > 6 && <span className="px-1 py-1 text-[11px] text-gray-600">+{n - 6} more</span>}
          </div>
        )}

        {!done && (
          <button type="button" onClick={onCancel} className="mt-7 inline-flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors">
            Cancel <kbd className="rounded border border-white/15 px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd>
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- Plan renderer ------------------------------ */
const Stat = ({ label, value, sub, tone = "text-white" }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className={`mt-1 text-lg sm:text-xl font-bold ${tone}`}>
      {value}
      {sub && <span className="ml-1 text-xs font-normal text-gray-500">{sub}</span>}
    </p>
  </div>
);

// Shared by the fresh result and the saved-plan popup. With startAt, times are
// real clock times; without it they are offsets from the start (0:00 → 0:40).
function PlanView({ result, budget, startAt, checked = {}, onToggleStep }) {
  const items = (result?.plan || []).slice().sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
  let cursor = 0;
  const rows = items.map((item, idx) => {
    const mins = Math.max(0, Number(item.estimate_minutes) || 0);
    const row = { item, idx, mins, from: cursor, to: cursor + mins, color: ACCENTS[idx % ACCENTS.length] };
    cursor += mins;
    return row;
  });
  const planned = Number(result?.metrics?.total_minutes) || cursor;
  const budgetNum = Number(budget) || 0;
  const scale = Math.max(cursor, budgetNum, 1);
  const buffer = budgetNum - planned;
  const at = (offset) => (startAt ? fmtClock(new Date(startAt.getTime() + offset * 60000)) : fmtOffset(offset));

  if (!rows.length && !result?.summary) {
    return <div className="py-10 text-center text-sm text-gray-500">No tasks in this plan.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Tasks" value={rows.length || result?.metrics?.tasks_count || 0} />
          <Stat label="Planned" value={fmtMinutes(planned)} sub={budgetNum ? `of ${fmtMinutes(budgetNum)}` : null} />
          {budgetNum > 0 && (
            <Stat label={buffer >= 0 ? "Buffer" : "Over by"} value={fmtMinutes(Math.abs(buffer))} tone={buffer >= 0 ? "text-emerald-300" : "text-amber-300"} />
          )}
        </div>
        {rows.length > 0 && (
          <div className="mt-4 flex h-2.5 w-full gap-[3px] overflow-hidden rounded-full bg-white/[0.05]">
            {rows.map((r) => (
              <div
                key={r.idx}
                title={`${r.item.task} · ${fmtMinutes(r.mins)}`}
                className="h-full rounded-full"
                style={{ width: `${(r.mins / scale) * 100}%`, background: r.color, boxShadow: `0 0 12px ${r.color}66` }}
              />
            ))}
          </div>
        )}
      </div>

      {result?.summary && (
        <div className="relative overflow-hidden rounded-2xl border border-purple-400/20 bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10 p-4 sm:p-5">
          <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-purple-300">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zm7 12l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9L19 14z" /></svg>
            AI insight
          </p>
          <p className="mt-2 text-sm sm:text-[15px] leading-relaxed text-gray-200">{result.summary}</p>
        </div>
      )}

      <ol className="relative space-y-3">
        <span aria-hidden="true" className="absolute left-4 top-6 bottom-6 w-px bg-gradient-to-b from-cyan-400/50 via-white/10 to-transparent" />
        {rows.map(({ item, idx, mins, from, to, color }) => {
          const steps = Array.isArray(item.steps) ? item.steps : [];
          const doneCount = steps.filter((_, si) => checked[`${idx}-${si}`]).length;
          return (
            <li key={idx} className="relative flex gap-3 sm:gap-4" style={{ animation: `sxFade .45s ease ${idx * 70}ms both` }}>
              <div
                className="relative z-10 mt-4 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 bg-[#0b0d11] font-mono text-xs font-bold"
                style={{ borderColor: color, color, boxShadow: idx === 0 ? `0 0 18px ${color}88` : "none" }}
              >
                {idx + 1}
              </div>
              <div className={`min-w-0 flex-1 rounded-2xl border p-4 sm:p-5 transition-colors ${idx === 0 ? "border-cyan-400/30 bg-cyan-400/[0.05]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"}`}>
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                  <div className="min-w-0">
                    {idx === 0 && (
                      <span className="mb-1.5 inline-block rounded-full bg-cyan-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-200">Start here</span>
                    )}
                    <h3 className="break-words text-base font-semibold leading-snug text-white">{item.task}</h3>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className="font-mono text-[11px] text-gray-400">{at(from)} → {at(to)}</span>
                    <span className="rounded-md px-2 py-0.5 text-xs font-medium" style={{ color, background: `${color}1a`, border: `1px solid ${color}40` }}>
                      {fmtMinutes(mins)}
                    </span>
                  </div>
                </div>
                {item.why && <p className="mt-2 text-sm leading-relaxed text-gray-400">{item.why}</p>}
                {steps.length > 0 && (
                  <div className="mt-3 border-t border-white/[0.06] pt-3">
                    {onToggleStep && (
                      <p className="mb-1.5 text-[11px] text-gray-500">{doneCount}/{steps.length} steps done</p>
                    )}
                    <div className="space-y-1">
                      {steps.map((s, si) => {
                        const key = `${idx}-${si}`;
                        const isDone = !!checked[key];
                        return onToggleStep ? (
                          <button
                            type="button"
                            key={si}
                            onClick={() => onToggleStep(key)}
                            className="flex w-full items-start gap-2.5 rounded-lg px-1 py-1 text-left text-sm transition-colors hover:bg-white/[0.04]"
                          >
                            <span className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors ${isDone ? "border-cyan-400 bg-cyan-400 text-black" : "border-white/25"}`}>
                              {isDone && (
                                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                              )}
                            </span>
                            <span className={isDone ? "text-gray-500 line-through" : "text-gray-300"}>{s}</span>
                          </button>
                        ) : (
                          <div key={si} className="flex items-start gap-2.5 px-1 py-0.5 text-sm text-gray-300">
                            <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full" style={{ background: color }} />
                            <span>{s}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ------------------------------- Card + Popup ------------------------------- */
const ShimmerCardButton = ({
  title,
  description,
  onClick,
  loadingTime = 800,
  className = "",
  dateTime,
  dateTitle,
  planData,
  planUrl,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [fetchedPlanData, setFetchedPlanData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), loadingTime);
    return () => clearTimeout(timer);
  }, [loadingTime]);

  useEffect(() => {
    if (!showPopup) return;
    const onKey = (e) => { if (e.key === "Escape") setShowPopup(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPopup]);

  const handleClick = (e) => {
    if (isLoading) return;
    if (planUrl && !fetchedPlanData) {
      setFetchLoading(true);
      setFetchError("");
      fetch(planUrl, { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
          const isJson = res.headers.get("content-type")?.includes("application/json");
          if (!isJson) throw new Error("Invalid response format");
          return res.json();
        })
        .then((data) => { setFetchedPlanData(data); setFetchLoading(false); })
        .catch((err) => { setFetchError(err.message || "Failed to load plan"); setFetchLoading(false); });
    }
    setShowPopup(true);
    if (onClick) onClick(e);
  };

  const closePopup = () => setShowPopup(false);
  const pd = planData || fetchedPlanData;

  if (isLoading) {
    return (
      <div className={`rounded-2xl border border-white/[0.08] bg-white/[0.02] p-4 animate-pulse ${className}`}>
        <div className="flex gap-3">
          <div className="h-9 w-9 rounded-xl bg-white/[0.06]" />
          <div className="flex-1">
            <div className="mb-2 h-3.5 w-3/4 rounded-full bg-white/[0.08]" />
            <div className="h-3 w-1/2 rounded-full bg-white/5" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        className={`group block w-full rounded-2xl bg-gradient-to-br from-white/10 to-white/5 p-px text-left transition-all hover:-translate-y-0.5 hover:from-cyan-400/50 hover:to-purple-500/40 hover:shadow-[0_10px_40px_-12px_rgba(34,211,238,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/50 ${className}`}
        {...props}
      >
        <div className="h-full rounded-2xl bg-[#0c0e12] p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-cyan-400/20 bg-cyan-400/10 text-cyan-300">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            </div>
            <div className="min-w-0 flex-1">
              {title && <h3 className="truncate text-sm font-semibold text-white">{title}</h3>}
              {description && <p className="mt-0.5 text-xs text-gray-400">{description}</p>}
            </div>
            <svg className="mt-1 h-4 w-4 flex-shrink-0 text-gray-600 transition-all group-hover:translate-x-0.5 group-hover:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          {dateTime && <p className="mt-3 text-[11px] text-gray-500" title={dateTitle}>{dateTime}</p>}
        </div>
      </button>

      {showPopup && (
        <div className="sx-motion fixed inset-0 z-[5000] flex items-center justify-center p-3 sm:p-6">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={closePopup} />
          <div className="relative w-full max-w-3xl rounded-3xl bg-gradient-to-br from-cyan-400/40 via-white/10 to-purple-500/30 p-px shadow-2xl" style={{ animation: "sxFade .3s ease" }}>
            <div className="overflow-hidden rounded-3xl bg-[#0b0d11]">
              <div className="flex items-start justify-between gap-4 border-b border-white/[0.08] px-5 py-4 sm:px-6">
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold text-white">{title}</h2>
                  <p className="mt-0.5 text-xs text-gray-500">{dateTitle || dateTime}</p>
                </div>
                <button onClick={closePopup} className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white/[0.08] hover:text-gray-300" aria-label="Close">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="max-h-[75vh] overflow-y-auto p-4 sm:p-6">
                {fetchLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-12 text-sm text-gray-400">
                    <SyncusiveMark className="h-20 w-20" />
                    Loading plan…
                  </div>
                ) : fetchError ? (
                  <div className="py-12 text-center text-sm text-red-400">⚠ {fetchError}</div>
                ) : pd ? (
                  <>
                    {(pd.input?.context || (pd.input?.constraints || []).length > 0) && (
                      <div className="mb-4 flex flex-wrap gap-2 text-xs">
                        {pd.input?.context && (
                          <span className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-2.5 py-1 text-cyan-200">{pd.input.context}</span>
                        )}
                        {(pd.input?.constraints || []).map((c, i) => (
                          <span key={i} className="rounded-full border border-amber-400/25 bg-amber-400/10 px-2.5 py-1 text-amber-200">{c}</span>
                        ))}
                      </div>
                    )}
                    <PlanView result={pd.result} budget={pd.input?.time_budget_minutes} />
                  </>
                ) : (
                  <div className="py-12 text-center text-sm text-gray-500">No plan data available.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* --------------------------- Glassy Login Wall UI --------------------------- */
function LoginWall({ onLogin }) {
  const [authError, setAuthError] = React.useState("");
  const [activeDemo, setActiveDemo] = React.useState(0);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const e = url.searchParams.get("auth_error") || url.searchParams.get("error");
      if (e) setAuthError(decodeURIComponent(e));
    } catch {}
  }, []);

  // Cycle through demo "tasks" to animate the blurred preview
  useEffect(() => {
    const t = setInterval(() => setActiveDemo(p => (p + 1) % 4), 2200);
    return () => clearInterval(t);
  }, []);

  const DEMO_PLAN = [
    { priority: 1, task: "Record & edit YouTube short", minutes: 25, score: 94, tag: "High Impact" },
    { priority: 2, task: "Write newsletter draft (500w)", minutes: 20, score: 88, tag: "Revenue" },
    { priority: 3, task: "Reply to partnership DMs", minutes: 10, score: 76, tag: "Growth" },
    { priority: 4, task: "Update landing page copy", minutes: 15, score: 71, tag: "Conversion" },
    { priority: 5, task: "Review analytics + log insights", minutes: 10, score: 65, tag: "Intel" },
  ];

  const FEATURES = [
    {
      icon: "🧠",
      color: "#22d3ee",
      title: "AI Priority Scoring",
      desc: "Every task gets an impact × urgency score. The AI reads between the lines — a task that 'seems small' but unblocks 3 others gets bumped up automatically.",
    },
    {
      icon: "⏱",
      color: "#a78bfa",
      title: "Time-Box Engine",
      desc: "Tell it you have 90 minutes. It assembles an airtight sequence that fits exactly — no overflow, no dead time, no guessing.",
    },
    {
      icon: "🔒",
      color: "#fb923c",
      title: "Constraint-Aware",
      desc: "Deadlines, banned tools, energy levels — the optimizer bakes them into every decision. It won't suggest a Canva task if you said 'no paid tools today'.",
    },
    {
      icon: "💾",
      color: "#2dd4bf",
      title: "Persistent Plans",
      desc: "Every plan is saved to your account. Come back tomorrow and resume where you left off, or compare last week's plan to see how your focus is evolving.",
    },
  ];

  const STATS = [
    { val: "< 2 min", label: "to generate a full day plan" },
    { val: "8+",      label: "variables analyzed per task" },
    { val: "100%",    label: "free during beta" },
    { val: "∞",       label: "plans saved to your account" },
  ];

  return (
    <div className="min-h-screen bg-black relative overflow-x-hidden text-white">

      {/* ── Background grid ── */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(0,255,255,0.12) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(0,255,255,0.12) 1px, transparent 1px)`,
          backgroundSize: "38px 38px",
          animation: "authGridDrift 28s linear infinite",
        }} />
      </div>
      <div className="pointer-events-none absolute -top-32 -left-32 w-[50rem] h-[50rem] rounded-full bg-cyan-500/[0.08] blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -right-24 w-[36rem] h-[36rem] rounded-full bg-purple-500/[0.08] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 left-1/4 w-[30rem] h-[30rem] rounded-full bg-cyan-400/[0.06] blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 py-16 space-y-24">

        {/* ══════════════════ HERO ══════════════════ */}
        <section className="text-center space-y-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-xs font-mono text-cyan-300 tracking-widest uppercase">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            🟢 Live Beta · Free Access
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
            Stop guessing<br />
            <span className="bg-gradient-to-r from-cyan-400 via-teal-300 to-purple-400 bg-clip-text text-transparent">
              what to work on next.
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-lg md:text-xl text-gray-300 leading-relaxed">
            Paste your task list. Set a time budget. The AI reads your full workload, weighs impact vs. urgency,
            and hands you a <strong className="text-white">ranked, time-boxed execution plan</strong> in under 2 minutes.
            Built for solo founders who wear 8 hats at once.
          </p>

          <div className="flex flex-wrap justify-center gap-3 text-sm text-gray-400">
            {["No spreadsheets", "No Notion templates", "No manual sorting", "Just your plan"].map(t => (
              <span key={t} className="flex items-center gap-1.5">
                <span className="text-cyan-400">✓</span> {t}
              </span>
            ))}
          </div>
        </section>

        {/* ══════════════════ BLURRED DEMO PREVIEW ══════════════════ */}
        <section className="relative">
          <div className="text-center mb-6">
            <p className="text-xs font-mono text-cyan-400/70 tracking-widest uppercase">
              ↓ This is what your plan looks like — sign in to unlock it ↓
            </p>
          </div>

          <div className="relative rounded-2xl border border-cyan-400/20 bg-white/[0.03] backdrop-blur-sm overflow-hidden">
            {/* Animated top accent line */}
            <div className="h-0.5 bg-gradient-to-r from-cyan-400 via-purple-400 to-cyan-400 animate-pulse" />

            {/* Mock header bar */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.08]">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="font-mono text-sm text-cyan-300 tracking-wider">NEURAL PIPELINE ANALYSIS</span>
              </div>
              <div className="flex gap-2">
                <span className="text-xs bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 px-2 py-0.5 rounded-full font-mono">5 tasks</span>
                <span className="text-xs bg-purple-400/10 border border-purple-400/20 text-purple-300 px-2 py-0.5 rounded-full font-mono">80 min</span>
              </div>
            </div>

            {/* Mock summary */}
            <div className="px-5 py-4 border-b border-white/[0.08]">
              <div className="text-sm text-gray-300 leading-relaxed blur-[3px] select-none">
                AI has analyzed your workload and identified 5 high-leverage tasks within your 80-minute window.
                Priority sequencing maximizes output while respecting your constraint: no paid tools today.
                Estimated completion confidence: 91%.
              </div>
            </div>

            {/* Mock plan rows */}
            <div className="divide-y divide-white/5">
              {DEMO_PLAN.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 px-5 py-4 transition-colors"
                  style={{
                    background: i === activeDemo ? "rgba(0,221,255,0.04)" : "transparent",
                    filter: i > 1 ? "blur(4px)" : "none",
                  }}
                >
                  <div className="w-7 h-7 rounded-full border flex items-center justify-center text-xs font-bold font-mono flex-shrink-0"
                    style={{
                      borderColor: i === activeDemo ? "#22d3ee" : "rgba(255,255,255,0.2)",
                      color: i === activeDemo ? "#22d3ee" : "rgba(255,255,255,0.5)",
                    }}>
                    {item.priority}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{item.task}</div>
                    <div className="text-xs text-gray-400 mt-0.5 font-mono">{item.minutes} min allocated</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded-full border font-mono"
                      style={{ borderColor: "rgba(0,221,255,0.2)", color: "#22d3ee", background: "rgba(0,221,255,0.06)" }}>
                      {item.tag}
                    </span>
                    <span className="text-sm font-bold font-mono" style={{ color: item.score > 85 ? "#22d3ee" : item.score > 70 ? "#a78bfa" : "#6b7280" }}>
                      {item.score}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Frosted unlock overlay on the bottom half */}
            <div className="absolute bottom-0 left-0 right-0 h-2/3 flex flex-col items-center justify-end pb-8"
              style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.7) 35%, rgba(0,0,0,0.95) 100%)" }}>
              <div className="text-center space-y-4 px-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-black/60 backdrop-blur-sm px-4 py-2 text-sm text-cyan-200">
                  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-cyan-400">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Sign in free to generate &amp; save your real plan
                </div>
                <p className="text-xs text-gray-400 max-w-xs">
                  Your plan is generated fresh every time, tuned to your exact tasks, time budget, and constraints.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════ STATS BAR ══════════════════ */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map(({ val, label }) => (
            <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 text-center">
              <div className="text-2xl md:text-3xl font-extrabold font-mono text-cyan-400">{val}</div>
              <div className="text-xs text-gray-400 mt-1 leading-snug">{label}</div>
            </div>
          ))}
        </section>

        {/* ══════════════════ FEATURE BREAKDOWN ══════════════════ */}
        <section className="space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              It doesn't just sort your list.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                It thinks about it.
              </span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto text-sm">
              Here's what happens under the hood every time you hit "Optimize":
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FEATURES.map(({ icon, color, title, desc }) => (
              <div key={title} className="rounded-xl border bg-white/[0.03] backdrop-blur-sm p-6 space-y-3 hover:bg-white/5 transition-colors"
                style={{ borderColor: color + "25" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: color + "15", border: `1px solid ${color}30` }}>
                    {icon}
                  </div>
                  <h3 className="font-bold text-base" style={{ color }}>{title}</h3>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════ HOW IT WORKS ══════════════════ */}
        <section className="space-y-8">
          <h2 className="text-center text-2xl md:text-3xl font-bold tracking-tight">
            From chaos to clarity in{" "}
            <span className="text-cyan-400">3 steps</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", icon: "📋", color: "#22d3ee", title: "Paste your tasks", desc: "Dump your full to-do list — rough, unformatted, messy. The AI handles the parsing. One task per line, any format." },
              { n: "02", icon: "⚙️", color: "#a78bfa", title: "Set your constraints", desc: "How many minutes do you have? Any hard deadlines? Tools you can't use today? The more context you give, the smarter the output." },
              { n: "03", icon: "🚀", color: "#2dd4bf", title: "Get your plan", desc: "A ranked, time-boxed execution sequence lands in seconds. Save it, share it, or start executing right now. No fluff, no filler." },
            ].map(({ n, icon, color, title, desc }) => (
              <div key={n} className="relative rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 space-y-4">
                <div className="text-4xl font-black font-mono opacity-10 absolute top-4 right-5" style={{ color }}>{n}</div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                  style={{ background: color + "15", border: `1px solid ${color}30` }}>
                  {icon}
                </div>
                <h3 className="font-bold text-base text-white">{title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════════════ LOGIN CARD ══════════════════ */}
        <section className="flex justify-center" id="login">
          <div className="w-full max-w-lg">
            <div className="text-center mb-6 space-y-2">
              <h2 className="text-2xl font-bold">Ready to optimize your day?</h2>
              <p className="text-gray-400 text-sm">Free during beta. No credit card. Takes 10 seconds.</p>
            </div>

            <div className="relative rounded-2xl border border-white/15 bg-white/5 backdrop-blur-2xl shadow-[0_0_0_1px_rgba(0,255,255,.08),0_40px_120px_-20px_rgba(0,255,255,.2)] overflow-hidden">
              <div className="h-0.5 bg-gradient-to-r from-cyan-400 via-emerald-400 to-purple-400 opacity-80 animate-pulse" />

              <div className="p-8 space-y-6">
                {authError && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                    {authError}
                  </div>
                )}

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={startGoogleLogin}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-xl px-5 py-4 bg-white text-black font-semibold text-base hover:bg-gray-100 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-cyan-400 shadow-lg"
                    aria-label="Sign in with Google"
                  >
                    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.5 32.9 29.2 36 24 36c-6.6 0-12-5.4-12-12S17.4 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c10.3 0 19-7.5 19-20 0-1.2-.1-2.3-.4-3.5z"/>
                      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6 29.4 4 24 4 16.1 4 9.1 8.6 6.3 14.7z"/>
                      <path fill="#4CAF50" d="M24 44c5.1 0 9.9-1.9 13.4-5.1l-6.2-5.2C29.1 35.9 26.7 36 24 36c-5.2 0-9.5-3.1-11.3-7.5l-6.5 5C9 39.1 15.9 44 24 44z"/>
                      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1 2.9-3 5.1-5.6 6.7l6.2 5.2C38.9 36.5 44 31 44 24c0-1.2-.1-2.3-.4-3.5z"/>
                    </svg>
                    Continue with Google — it's free
                  </button>

                  <p className="text-center text-xs text-gray-500">
                    By continuing, you agree to our{" "}
                    <span className="underline decoration-dotted text-gray-400">Terms</span> &{" "}
                    <span className="underline decoration-dotted text-gray-400">Privacy Policy</span>.
                    We only read your email address — nothing else.
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-2 border-t border-white/[0.08]">
                  {[
                    { icon: "🔒", text: "Email only — no posting" },
                    { icon: "⚡", text: "Instant access" },
                    { icon: "🗑", text: "Delete data anytime" },
                  ].map(({ icon, text }) => (
                    <div key={text} className="text-center space-y-1">
                      <div className="text-lg">{icon}</div>
                      <div className="text-xs text-gray-400 leading-tight">{text}</div>
                    </div>
                  ))}
                </div>

                <details className="text-sm text-gray-400">
                  <summary className="cursor-pointer select-none hover:text-gray-200 transition-colors">
                    Having trouble signing in?
                  </summary>
                  <ul className="mt-2 list-disc list-inside space-y-1 text-xs text-gray-500 pl-2">
                    <li>Disable popup blockers for this site</li>
                    <li>Try a private / incognito window</li>
                    <li>Ensure your system clock is accurate</li>
                  </ul>
                </details>
              </div>
            </div>
          </div>
        </section>

      </div>{/* /max-w-6xl */}

      <style>{`
        @keyframes gridMove { 0% { transform: translate(0,0); } 100% { transform: translate(38px,38px); } }
      `}</style>
    </div>
  );
}


/* ------------------------------- Main Component ------------------------------ */
const StepLabel = ({ n, title, hint, right }) => (
  <div className="mb-4 flex items-center justify-between gap-3">
    <div className="flex min-w-0 items-center gap-3">
      <span className="rounded-md border border-cyan-400/20 bg-cyan-400/10 px-1.5 py-0.5 font-mono text-[11px] text-cyan-300">{n}</span>
      <span className="text-sm font-semibold text-white">{title}</span>
      {hint && <span className="hidden truncate text-xs text-gray-500 sm:inline">{hint}</span>}
    </div>
    {right}
  </div>
);

const Kbd = ({ children }) => (
  <kbd className="rounded border border-white/15 bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-gray-400">{children}</kbd>
);

export default function TaskOptimizer() {
  const initial = readLS();
  const [tasks, setTasks] = useState(
    initial?.tasks?.length ? initial.tasks : ["Write blog on AI OS", "Edit YouTube short"]
  );
  const [timeBudget, setTimeBudget] = useState(initial?.timeBudget ?? 120);
  const [constraints, setConstraints] = useState(
    initial?.constraints ?? ["No paid tools", "Publish today"]
  );
  const [constraintDraft, setConstraintDraft] = useState("");
  const [context, setContext] = useState(
    initial?.context ?? "Solopreneur content pipeline"
  );
  const [status, setStatus] = useState("idle");
  const [loaderDone, setLoaderDone] = useState(false);
  const [result, setResult] = useState(null);
  const [resultBudget, setResultBudget] = useState(0);
  const [planStart, setPlanStart] = useState(null);
  const [checked, setChecked] = useState({});
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const [auth, setAuth] = useState({ authenticated: false, user: null });
  const [authChecked, setAuthChecked] = useState(false);
  const [plans, setPlans] = useState([]);
  const [showAllPlans, setShowAllPlans] = useState(false);

  const taskRefs = useRef([]);
  const focusIdx = useRef(null);
  const abortRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(
      () => writeLS({ tasks, timeBudget, constraints, context }),
      200
    );
    return () => clearTimeout(id);
  }, [tasks, timeBudget, constraints, context]);

  // Focus the task input that Enter / Backspace / "Add task" just created or moved to
  useEffect(() => {
    if (focusIdx.current == null) return;
    taskRefs.current[focusIdx.current]?.focus();
    focusIdx.current = null;
  }, [tasks]);

  useEffect(() => {
    if (result && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const refreshAuth = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/status`, {
        credentials: "include",
      });
      const data = await res.json();
      setAuth(data);
      if (data.authenticated) {
        const r = await fetch(`${API_BASE}/api/plans`, {
          credentials: "include",
        });
        if (r.ok) {
          const list = await r.json();
          setPlans(list.items || []);
        } else {
          setPlans([]);
        }
      } else {
        setPlans([]);
      }
    } catch {
      setAuth({ authenticated: false, user: null });
      setPlans([]);
    } finally {
      setAuthChecked(true);
    }
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  /* ------------------------------ Task editing ------------------------------ */
  const addTask = () => {
    focusIdx.current = tasks.length;
    setTasks((t) => [...t, ""]);
  };
  const updateTask = (idx, value) =>
    setTasks((t) => t.map((x, i) => (i === idx ? value : x)));
  const removeTask = (idx) =>
    setTasks((t) => (t.length > 1 ? t.filter((_, i) => i !== idx) : [""]));
  const onTaskKeyDown = (i) => (e) => {
    if (e.key === "Enter" && !(e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      focusIdx.current = i + 1;
      setTasks((t) => [...t.slice(0, i + 1), "", ...t.slice(i + 1)]);
    } else if (e.key === "Backspace" && tasks[i] === "" && tasks.length > 1) {
      e.preventDefault();
      focusIdx.current = Math.max(0, i - 1);
      removeTask(i);
    }
  };

  /* --------------------------- Constraint chips ----------------------------- */
  const cleanConstraints = [...new Set(constraints.map((c) => c.trim()).filter(Boolean))];
  const addConstraint = (raw) => {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    setConstraints((prev) => {
      const base = prev.map((c) => c.trim()).filter(Boolean);
      const lower = base.map((c) => c.toLowerCase());
      return [...base, ...parts.filter((p) => !lower.includes(p.toLowerCase()))];
    });
  };
  const removeConstraint = (c) => setConstraints((prev) => prev.filter((x) => x.trim() !== c));
  const onConstraintChange = (e) => {
    const v = e.target.value;
    if (v.includes(",")) {
      addConstraint(v);
      setConstraintDraft("");
    } else {
      setConstraintDraft(v.replace(/^\s+/, ""));
    }
  };
  const onConstraintKeyDown = (e) => {
    if (e.key === "Enter" && !(e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addConstraint(constraintDraft);
      setConstraintDraft("");
    } else if (e.key === "Backspace" && !constraintDraft && cleanConstraints.length) {
      removeConstraint(cleanConstraints[cleanConstraints.length - 1]);
    }
  };
  const lowerConstraints = cleanConstraints.map((c) => c.toLowerCase());
  const suggestions = CONSTRAINT_SUGGESTIONS.filter((s) => !lowerConstraints.includes(s.toLowerCase()));

  /* -------------------------------- Submit --------------------------------- */
  const cancelOptimize = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus("idle");
    setLoaderDone(false);
  }, []);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (status === "loading") return;

    if (!auth?.authenticated) {
      setStatus("error");
      setError("Please log in to use the Task Optimizer.");
      return;
    }

    const cleanedTasks = tasks.map((t) => t.trim()).filter(Boolean);
    if (!cleanedTasks.length) {
      setStatus("error");
      setError("Please add at least one task.");
      return;
    }
    const budgetNum = Number(timeBudget);
    if (!Number.isFinite(budgetNum) || budgetNum <= 0) {
      setStatus("error");
      setError("Time budget must be a positive number.");
      return;
    }
    const draftParts = constraintDraft.split(",").map((s) => s.trim()).filter(Boolean);
    const finalConstraints = [...new Set([...cleanConstraints, ...draftParts])];
    if (draftParts.length) {
      setConstraints(finalConstraints);
      setConstraintDraft("");
    }

    setStatus("loading");
    setLoaderDone(false);
    setError("");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const started = Date.now();

    try {
      const res = await fetch(`${API_BASE}/api/optimize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        signal: ctrl.signal,
        body: JSON.stringify({
          tasks: cleanedTasks,
          time_budget_minutes: budgetNum,
          constraints: finalConstraints,
          context: (context || "").trim(),
        }),
      });
      const isJson = res.headers.get("content-type")?.includes("application/json");
      const data = isJson ? await res.json() : null;
      if (!res.ok)
        throw new Error(
          (data && (data.message || data.error)) ||
            `${res.status} ${res.statusText}`
        );

      // Keep the loader up long enough to read, then flash "ready" before revealing the plan
      const wait = 1400 - (Date.now() - started);
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      if (ctrl.signal.aborted) return;
      setLoaderDone(true);
      await new Promise((r) => setTimeout(r, 800));
      if (ctrl.signal.aborted) return;

      setPlanStart(nextFiveMinutes());
      setResultBudget(budgetNum);
      setChecked({});
      setResult(data || {});
      setStatus("done");
      refreshAuth();
    } catch (err) {
      if (err?.name === "AbortError") return;
      setError(err?.message || "Something went wrong");
      setStatus("error");
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  };

  const onFormKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      onSubmit();
    } else if (e.target.tagName === "INPUT") {
      e.preventDefault(); // plain Enter never fires a generation by accident
    }
  };

  const copyPlan = async () => {
    const items = (result?.plan || []).slice().sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0));
    let cursor = 0;
    const lines = items.map((it, i) => {
      const mins = Number(it.estimate_minutes) || 0;
      const from = planStart ? fmtClock(new Date(planStart.getTime() + cursor * 60000)) : fmtOffset(cursor);
      cursor += mins;
      const to = planStart ? fmtClock(new Date(planStart.getTime() + cursor * 60000)) : fmtOffset(cursor);
      const steps = (Array.isArray(it.steps) ? it.steps : []).map((s) => `   - ${s}`).join("\n");
      return `${i + 1}. ${it.task} (${fmtMinutes(mins)}, ${from}–${to})${it.why ? `\n   ${it.why}` : ""}${steps ? `\n${steps}` : ""}`;
    });
    const text = [`My plan — ${fmtMinutes(cursor)} of ${fmtMinutes(resultBudget)}`, "", ...lines, result?.summary ? `\n${result.summary}` : ""].join("\n").trim();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  /* ------------------------------ Auth states ------------------------------- */
  if (!authChecked) {
    return (
      <div className="sx-motion flex min-h-[70vh] items-center justify-center bg-[#0a0a0a]">
        <style>{SX_KEYFRAMES}</style>
        <SyncusiveMark className="h-24 w-24" />
      </div>
    );
  }

  if (!auth?.authenticated) {
    return <LoginWall onLogin={startGoogleLogin} />;
  }

  /* --------------------------------- Authenticated UI -------------------------------- */
  const filledTasks = tasks.map((t) => t.trim()).filter(Boolean);
  const budgetNum = Number(timeBudget) || 0;
  const firstName = (auth?.user?.name || "").split(" ")[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const ringPct = Math.min(1, budgetNum / 480); // ring fills over an 8h day
  const visiblePlans = showAllPlans ? plans : plans.slice(0, 6);

  return (
    <div className="sx-motion relative min-h-screen bg-[#0a0a0a] text-white">
      <style>{SX_KEYFRAMES}</style>

      {/* Background: drifting grid + aurora glows */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "linear-gradient(rgba(0,221,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,221,255,0.04) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          animation: "authGridDrift 40s linear infinite",
        }} />
        <div className="absolute -top-40 left-1/4 h-[520px] w-[520px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.12), transparent 70%)" }} />
        <div className="absolute top-1/3 -right-40 h-[480px] w-[480px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(167,139,250,0.10), transparent 70%)" }} />
      </div>

      <OptimizingOverlay
        open={status === "loading"}
        done={loaderDone}
        tasks={filledTasks}
        budget={budgetNum}
        constraintsCount={cleanConstraints.length}
        onCancel={cancelOptimize}
      />

      <main className="relative z-10 px-4 py-10 sm:py-14">
        <div className="mx-auto w-full max-w-6xl space-y-8">

          {/* Header */}
          <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-400/[0.07] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-400" />
                </span>
                AI Planner · Live beta
              </div>
              <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
                Task{" "}
                <span className="bg-gradient-to-r from-cyan-300 via-teal-200 to-purple-300 bg-clip-text text-transparent">Optimizer</span>
              </h1>
              <p className="mt-2 max-w-xl text-gray-400">
                Dump everything on your plate, set your time, and let Syncusive AI hand you a ranked, time-boxed plan.
              </p>
            </div>
            {firstName && (
              <div className="self-start rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur sm:self-auto">
                <p className="text-sm text-white">{greeting}, {firstName} 👋</p>
                <p className="mt-0.5 text-xs text-gray-500">{plans.length} plan{plans.length === 1 ? "" : "s"} saved</p>
              </div>
            )}
          </header>

          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            {/* ─────────── Left: form + results ─────────── */}
            <div className="min-w-0 space-y-8">
              <div className="rounded-3xl bg-gradient-to-br from-cyan-400/40 via-white/10 to-purple-500/30 p-px shadow-[0_0_60px_-15px_rgba(34,211,238,0.35)]">
                <div className="rounded-3xl bg-[#0b0d11]/95 backdrop-blur-xl">
                  <form onSubmit={onSubmit} onKeyDown={onFormKeyDown}>

                    {/* 01 Tasks */}
                    <div className="p-5 sm:p-7">
                      <StepLabel
                        n="01"
                        title="What's on your plate?"
                        right={<span className="flex-shrink-0 text-xs text-gray-500">{filledTasks.length} task{filledTasks.length === 1 ? "" : "s"}</span>}
                      />
                      <div className="space-y-2.5">
                        {tasks.map((t, i) => {
                          const c = ACCENTS[i % ACCENTS.length];
                          return (
                            <div
                              key={i}
                              className="group flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 transition-all hover:border-white/20 focus-within:border-cyan-400/50 focus-within:bg-cyan-400/[0.04] focus-within:shadow-[0_0_0_4px_rgba(34,211,238,0.08)]"
                            >
                              <span
                                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md font-mono text-[11px]"
                                style={{ color: c, background: `${c}1a`, border: `1px solid ${c}33` }}
                              >
                                {i + 1}
                              </span>
                              <input
                                ref={(el) => { taskRefs.current[i] = el; }}
                                value={t}
                                onChange={(e) => updateTask(i, e.target.value)}
                                onKeyDown={onTaskKeyDown(i)}
                                placeholder={i === 0 ? "e.g. Ship the pricing page" : "Next task…"}
                                className="min-w-0 flex-1 bg-transparent py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => removeTask(i)}
                                className="rounded-lg p-1.5 text-gray-600 transition-all hover:bg-red-500/10 hover:text-red-400 focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                aria-label={`Remove task ${i + 1}`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={addTask}
                        className="mt-2.5 flex w-full items-center justify-between gap-2 rounded-xl border border-dashed border-white/15 px-3 py-2.5 text-sm text-gray-400 transition-colors hover:border-cyan-400/40 hover:bg-cyan-400/[0.03] hover:text-cyan-300"
                      >
                        <span className="flex items-center gap-2"><span className="text-base leading-none">+</span> Add task</span>
                        <span className="hidden sm:inline"><Kbd>Enter ↵</Kbd></span>
                      </button>
                    </div>

                    {/* 02 Time + context */}
                    <div className="grid gap-6 border-t border-white/[0.06] p-5 sm:grid-cols-2 sm:p-7">
                      <div>
                        <StepLabel n="02" title="Time budget" right={<span className="flex-shrink-0 text-xs text-cyan-300">{fmtMinutes(budgetNum)}</span>} />
                        <div className="flex flex-wrap gap-2">
                          {BUDGET_PRESETS.map((m) => {
                            const active = budgetNum === m;
                            return (
                              <button
                                type="button"
                                key={m}
                                onClick={() => setTimeBudget(m)}
                                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-all ${active
                                  ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-200 shadow-[0_0_16px_rgba(34,211,238,0.25)]"
                                  : "border-white/10 bg-white/[0.03] text-gray-400 hover:border-white/20 hover:text-white"}`}
                              >
                                {fmtMinutes(m)}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-3 flex items-center rounded-xl border border-white/10 bg-white/[0.03] px-3 transition-colors focus-within:border-cyan-400/50">
                          <input
                            type="number"
                            min={15}
                            step={5}
                            value={timeBudget}
                            onChange={(e) => setTimeBudget(e.target.value)}
                            aria-label="Time budget in minutes"
                            className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-white focus:outline-none"
                          />
                          <span className="text-xs text-gray-500">minutes</span>
                        </div>
                      </div>
                      <div>
                        <StepLabel n="03" title="Context" hint="optional" />
                        <div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-3 transition-colors focus-within:border-cyan-400/50">
                          <svg className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                          <input
                            value={context}
                            onChange={(e) => setContext(e.target.value)}
                            placeholder="e.g. Launch week, focus on lead gen"
                            className="min-w-0 flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none"
                          />
                        </div>
                        <p className="mt-2.5 text-xs leading-relaxed text-gray-500">
                          What's the bigger goal today? The AI uses it to break ties between tasks.
                        </p>
                      </div>
                    </div>

                    {/* 04 Constraints */}
                    <div className="border-t border-white/[0.06] p-5 sm:p-7">
                      <StepLabel n="04" title="Constraints" hint="Enter or comma to add" />
                      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2 transition-colors focus-within:border-cyan-400/50">
                        {cleanConstraints.map((c) => (
                          <span key={c} className="inline-flex items-center gap-1 rounded-lg border border-amber-400/25 bg-amber-400/10 py-1 pl-2.5 pr-1 text-xs text-amber-200">
                            {c}
                            <button type="button" onClick={() => removeConstraint(c)} className="rounded p-0.5 hover:bg-white/10" aria-label={`Remove ${c}`}>
                              <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </span>
                        ))}
                        <input
                          value={constraintDraft}
                          onChange={onConstraintChange}
                          onKeyDown={onConstraintKeyDown}
                          onBlur={() => { if (constraintDraft.trim()) { addConstraint(constraintDraft); setConstraintDraft(""); } }}
                          placeholder={cleanConstraints.length ? "Add more…" : "e.g. No paid tools, Publish today"}
                          className="min-w-[9rem] flex-1 bg-transparent px-1.5 py-1 text-sm text-white placeholder:text-gray-600 focus:outline-none"
                        />
                      </div>
                      {suggestions.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {suggestions.map((s) => (
                            <button
                              type="button"
                              key={s}
                              onClick={() => addConstraint(s)}
                              className="rounded-full border border-dashed border-white/15 px-2.5 py-1 text-xs text-gray-400 transition-colors hover:border-amber-400/40 hover:text-amber-200"
                            >
                              + {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Submit */}
                    <div className="flex flex-col gap-3 border-t border-white/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between sm:p-7">
                      <p className="order-2 flex items-center justify-center gap-2 text-xs text-gray-500 sm:order-1 sm:justify-start">
                        <span className="hidden sm:inline"><Kbd>Ctrl</Kbd> + <Kbd>Enter</Kbd> to generate ·</span> Saved to your account
                      </p>
                      <button
                        type="submit"
                        disabled={status === "loading"}
                        className="group relative order-1 w-full overflow-hidden rounded-xl bg-gradient-to-r from-cyan-400 via-teal-300 to-cyan-400 px-7 py-3 text-sm font-bold text-black shadow-[0_0_30px_rgba(34,211,238,0.35)] transition-shadow hover:shadow-[0_0_45px_rgba(34,211,238,0.6)] disabled:cursor-not-allowed disabled:opacity-60 sm:order-2 sm:w-auto"
                      >
                        <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                        <span className="relative inline-flex items-center justify-center gap-2">
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zm7 12l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9L19 14z" /></svg>
                          {result ? "Regenerate plan" : "Generate my plan"}
                          <span className="transition-transform group-hover:translate-x-0.5">→</span>
                        </span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-3 rounded-xl border border-red-500/20 bg-red-500/[0.06] px-4 py-3 text-sm text-red-400" style={{ animation: "sxFade .3s ease" }}>
                  <span className="mt-0.5 flex-shrink-0">⚠</span>
                  {error}
                </div>
              )}

              {result && (
                <section ref={resultsRef} className="scroll-mt-24 space-y-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-300">● Plan ready</p>
                      <h2 className="mt-1 text-2xl font-bold text-white">Your optimized day</h2>
                      {planStart && <p className="mt-0.5 text-sm text-gray-400">Starting at {fmtClock(planStart)}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={copyPlan}
                      className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 py-2 text-xs font-medium text-gray-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-200"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={copied ? "M5 13l4 4L19 7" : "M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"} /></svg>
                      {copied ? "Copied!" : "Copy plan"}
                    </button>
                  </div>
                  <PlanView
                    result={result}
                    budget={resultBudget}
                    startAt={planStart}
                    checked={checked}
                    onToggleStep={(key) => setChecked((c) => ({ ...c, [key]: !c[key] }))}
                  />
                </section>
              )}
            </div>

            {/* ─────────── Right: live snapshot (desktop) ─────────── */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">Session snapshot</p>
                  <div className="relative mx-auto mt-4 h-36 w-36">
                    <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                      <defs>
                        <linearGradient id="to-ring" x1="0" y1="0" x2="1" y2="1">
                          <stop offset="0" stopColor="#22d3ee" />
                          <stop offset="1" stopColor="#a78bfa" />
                        </linearGradient>
                      </defs>
                      <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
                      <circle
                        cx="60" cy="60" r="52" fill="none" stroke="url(#to-ring)" strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${ringPct * 326.7} 326.7`}
                        style={{ transition: "stroke-dasharray .5s ease", filter: "drop-shadow(0 0 6px rgba(34,211,238,0.5))" }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-white">{fmtMinutes(budgetNum)}</span>
                      <span className="text-[11px] text-gray-500">time budget</span>
                    </div>
                  </div>
                  <dl className="mt-5 space-y-2.5 text-sm">
                    <div className="flex justify-between"><dt className="text-gray-500">Tasks</dt><dd className="font-medium text-white">{filledTasks.length}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Avg. per task</dt><dd className="font-medium text-white">{filledTasks.length ? fmtMinutes(budgetNum / filledTasks.length) : "—"}</dd></div>
                    <div className="flex justify-between"><dt className="text-gray-500">Constraints</dt><dd className="font-medium text-white">{cleanConstraints.length}</dd></div>
                  </dl>
                </div>
                <div className="rounded-2xl border border-purple-400/15 bg-gradient-to-br from-purple-500/10 to-transparent p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-purple-300">Pro tips</p>
                  <ul className="mt-3 space-y-2.5 text-xs leading-relaxed text-gray-400">
                    <li>Put deadlines in the task itself, e.g. <span className="text-gray-200">"Send invoice (due 5pm)"</span>.</li>
                    <li>Press <Kbd>Enter</Kbd> in a task to add the next one, <Kbd>⌫</Kbd> on an empty one to remove it.</li>
                    <li>Tick off steps in your plan as you go.</li>
                  </ul>
                </div>
              </div>
            </aside>
          </div>

          {/* Saved plans */}
          {plans.length > 0 && (
            <section className="space-y-4 pt-2">
              <div className="flex items-end justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Saved plans</h3>
                  <p className="text-xs text-gray-500">Pick up where you left off</p>
                </div>
                {plans.length > 6 && (
                  <button type="button" onClick={() => setShowAllPlans((v) => !v)} className="text-xs text-cyan-400 transition-colors hover:text-cyan-300">
                    {showAllPlans ? "Show less" : `View all ${plans.length} →`}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {visiblePlans.map((p) => (
                  <ShimmerCardButton
                    key={p._id}
                    dateTime={timeAgo(p.created_at)}
                    dateTitle={new Date(p.created_at).toLocaleString()}
                    title={p.context || "Optimization plan"}
                    description={`${p.tasks_count || 0} task${p.tasks_count === 1 ? "" : "s"} · ${fmtMinutes(p.time_budget_minutes)}`}
                    planUrl={`${API_BASE}/api/plans/${p._id}`}
                  />
                ))}
              </div>
            </section>
          )}

        </div>
      </main>
    </div>
  );
}
